import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { useAuth } from "./AuthContext";
import api, { fmtQual } from "../lib/api";
import { Fingerprint, Delete, ShieldCheck, Clock, LogOut, Building2, Lock } from "lucide-react";

const FirmaCtx = createContext(null);
export const useFirma = () => useContext(FirmaCtx) || { firmaEnabled: false, activeOperatore: null, opName: "", identity: { operatore_id: null, operatore_nome: "" }, signerName: "", signerId: null, canSign: false, mustUnlock: false, isPrivileged: false };

// La Firma Rapida si applica agli account operativi/struttura (tablet condiviso).
// Gli account admin/owner (uso personale sul proprio PC) NON richiedono la Firma Rapida.
const FIRMA_ROLES = ["oss", "medico", "segreteria"];
const IDLE_MS = 2 * 60 * 1000; // 2 minuti
const STORAGE_KEY = "gr_firma_op";

export function FirmaProvider({ children }) {
  const { user, selectedStructure, structures, logout, setOpSections, companyLogo } = useAuth();
  const firmaEnabled = !!user && FIRMA_ROLES.includes(user.role) && user.richiedi_pin !== false && !user.firma_personale;
  // Kiosk tablet condiviso: nessun operatore pre-attivo al caricamento — si sblocca sempre col PIN.
  const [activeOperatore, setActiveOperatore] = useState(null);
  const [locked, setLocked] = useState(false);
  const timer = useRef(null);
  const lastActivityRef = useRef(Date.now());
  const activeOpRef = useRef(null);
  useEffect(() => { activeOpRef.current = activeOperatore; }, [activeOperatore]);
  const closeSession = () => {
    const op = activeOpRef.current;
    if (op) api.post("/firma/close", { operatore_id: op.id, operatore: `${op.nome} ${op.cognome}`.trim(), structure_id: op.structure_id }).catch(() => {});
  };

  const structureName = (() => {
    const list = structures || [];
    if (selectedStructure && selectedStructure !== "all") {
      const s = list.find((x) => x.id === selectedStructure);
      if (s) return s.nome;
    }
    if (list.length === 1) return list[0].nome;
    return list.length ? "Tutte le strutture" : "";
  })();

  useEffect(() => {
    if (activeOperatore) localStorage.setItem(STORAGE_KEY, JSON.stringify(activeOperatore));
    else localStorage.removeItem(STORAGE_KEY);
  }, [activeOperatore]);

  // Sicurezza: se l'operatore salvato non appartiene alle strutture dell'account
  // attualmente loggato (es. cambio account), chiudi la sua sessione.
  useEffect(() => {
    if (!activeOperatore || !(structures && structures.length)) return;
    const ids = structures.map((s) => s.id);
    const opStruct = activeOperatore.structure_id
      || (activeOperatore.structure_ids && activeOperatore.structure_ids[0]);
    if (opStruct && !ids.includes(opStruct)) {
      setActiveOperatore(null);
      setOpSections?.([]);
      if (firmaEnabled) setLocked(true);
    }
  }, [activeOperatore, structures, setOpSections, firmaEnabled]);

  const lock = useCallback(() => { closeSession(); setActiveOperatore(null); setOpSections?.([]); setLocked(true); }, [setOpSections]);

  const resetTimer = useCallback(() => {
    lastActivityRef.current = Date.now();
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => lock(), IDLE_MS);
  }, [lock]);

  // Blocco iniziale: sui tablet (Firma Rapida) mostra SEMPRE la schermata PIN all'avvio/refresh,
  // senza operatore pre-selezionato, così ognuno riseleziona il proprio nome.
  useEffect(() => {
    if (!firmaEnabled) { setLocked(false); return; }
    setActiveOperatore(null);
    setOpSections?.([]);
    setLocked(true);
  }, [firmaEnabled]); // eslint-disable-line react-hooks/exhaustive-deps

  // Blocco automatico quando il tablet va in background / schermo spento (rientro = schermata PIN)
  useEffect(() => {
    if (!firmaEnabled) return;
    const onVis = () => { if (document.hidden && activeOpRef.current) lock(); };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [firmaEnabled, lock]);

  // Rilevamento inattività — il movimento del mouse / pointer mantiene viva la sessione
  useEffect(() => {
    if (!firmaEnabled || locked) { if (timer.current) clearTimeout(timer.current); return; }
    const evts = ["mousedown", "keydown", "touchstart", "click", "scroll", "mousemove", "pointermove"];
    let lastReset = 0;
    const handler = () => { const now = Date.now(); if (now - lastReset < 700) return; lastReset = now; resetTimer(); };
    evts.forEach((e) => window.addEventListener(e, handler, { passive: true }));
    resetTimer();
    return () => {
      evts.forEach((e) => window.removeEventListener(e, handler));
      if (timer.current) clearTimeout(timer.current);
    };
  }, [firmaEnabled, locked, resetTimer]);

  const unlock = (op) => { setActiveOperatore(op); setOpSections?.(op?.sections || []); setLocked(false); };
  const change = () => { closeSession(); setActiveOperatore(null); setOpSections?.([]); setLocked(true); };

  // Aggiornamento automatico dei permessi dell'operatore attivo: se l'admin modifica
  // i permessi mentre l'operatore è già entrato col PIN, le sezioni si aggiornano
  // entro ~60s (e al rientro sulla scheda) senza dover re-inserire il PIN.
  const opIdActive = activeOperatore?.id;
  useEffect(() => {
    if (!opIdActive) return;
    let cancel = false;
    const refresh = async () => {
      try {
        const { data } = await api.get(`/firma/sections/${opIdActive}`);
        if (!cancel && Array.isArray(data?.sections)) {
          setOpSections?.(data.sections);
          setActiveOperatore((prev) => (prev && prev.id === opIdActive ? { ...prev, sections: data.sections } : prev));
        }
      } catch { /* noop */ }
    };
    refresh();
    const iv = setInterval(refresh, 60000);
    const onFocus = () => refresh();
    window.addEventListener("focus", onFocus);
    return () => { cancel = true; clearInterval(iv); window.removeEventListener("focus", onFocus); };
  }, [opIdActive, setOpSections]);

  // Account personale admin/owner: nella Firma Rapida mostra SOLO il proprio nominativo
  const restrictOperatoreId = user?.role === "admin" ? (user?.operatore_id || null) : null;

  const opName = activeOperatore ? `${activeOperatore.nome} ${activeOperatore.cognome}`.trim() : "";
  const identity = activeOperatore
    ? { operatore_id: activeOperatore.id, operatore_nome: opName }
    : (user ? { operatore_id: user.operatore_id || user.id, operatore_nome: user.name } : { operatore_id: null, operatore_nome: "" });

  // Account privilegiati (admin/super_admin): possono firmare/registrare senza PIN, con traccia del proprio nome + ruolo.
  const ROLE_LABELS = { admin: "Amministratore", super_admin: "Super Admin" };
  const isPrivileged = !!user && (user.role === "admin" || user.role === "super_admin" || !!user.firma_personale);
  const signerName = activeOperatore ? opName : (isPrivileged ? ((user.name || "").trim() || "Amministrazione") : "");
  const signerId = activeOperatore ? activeOperatore.id : (isPrivileged ? (user.operatore_id || user.id) : null);
  const canSign = !!activeOperatore || isPrivileged;
  const mustUnlock = firmaEnabled && !activeOperatore;

  return (
    <FirmaCtx.Provider value={{ firmaEnabled, activeOperatore, opName, identity, signerName, signerId, canSign, mustUnlock, isPrivileged, change, lock, keepAlive: resetTimer, lastActivityRef, idleMs: IDLE_MS }}>
      {children}
      {firmaEnabled && locked && (
        <LockScreen onUnlock={unlock} structureId={selectedStructure} structureName={structureName}
          accountEmail={user?.email} accountName={user?.name} onLogout={logout} restrictOperatoreId={restrictOperatoreId} logo={companyLogo} />
      )}
    </FirmaCtx.Provider>
  );
}

export function IdleBar() {
  const { firmaEnabled, activeOperatore, lastActivityRef, idleMs, keepAlive } = useFirma();
  const total = idleMs || IDLE_MS;
  const [rem, setRem] = useState(total);
  const notifiedRef = useRef(false);
  useEffect(() => {
    if (!firmaEnabled || !activeOperatore || !lastActivityRef) return;
    const tick = () => setRem(Math.max(0, total - (Date.now() - lastActivityRef.current)));
    tick();
    const iv = setInterval(tick, 1000);
    return () => clearInterval(iv);
  }, [firmaEnabled, activeOperatore, lastActivityRef, total]);
  // Notifica desktop best-effort quando si entra negli ultimi 15s (se il permesso è già concesso)
  const showWarn = firmaEnabled && !!activeOperatore && rem <= 15000 && rem > 0;
  useEffect(() => {
    if (showWarn) {
      if (!notifiedRef.current) {
        notifiedRef.current = true;
        try { if ("Notification" in window && Notification.permission === "granted") new Notification("Sessione in scadenza", { body: "Tocca l'app per restare connesso" }); } catch { /* noop */ }
      }
    } else { notifiedRef.current = false; }
  }, [showWarn]);
  if (!firmaEnabled || !activeOperatore) return null;
  const pct = Math.max(0, Math.min(100, (rem / total) * 100));
  const secs = Math.ceil(rem / 1000);
  const warn = rem <= 30000;
  return (
    <>
      <div className="hidden md:flex flex-col items-end gap-1" data-testid="firma-idle-timer" title="Tempo prima del blocco per inattività">
        <div className="flex items-center gap-1 text-[11px] font-bold tabnum" style={{ color: warn ? "#dc2626" : "#64748b" }}>
          <Clock size={12} /> {Math.floor(secs / 60)}:{String(secs % 60).padStart(2, "0")}
        </div>
        <div className="h-1.5 w-24 rounded-full bg-slate-200 overflow-hidden">
          <div className="h-full rounded-full transition-[width] duration-1000 ease-linear" style={{ width: pct + "%", background: warn ? "#dc2626" : "#1f7a5c" }} />
        </div>
      </div>
      {showWarn && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/50 p-4" data-testid="firma-idle-warning">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 text-center">
            <div className="mx-auto mb-3 grid place-items-center h-14 w-14 rounded-full bg-amber-100 text-amber-600"><Clock size={28} /></div>
            <h3 className="font-head font-bold text-lg">Sessione in scadenza</h3>
            <p className="text-sm text-muted-foreground mt-1">La schermata si bloccherà tra <b className="text-red-600 tabnum" data-testid="firma-idle-warning-secs">{secs}s</b> per inattività.</p>
            <p className="text-xs text-muted-foreground mt-1">Se sei presente, tocca il pulsante (o muovi il mouse) per restare connesso.</p>
            <button onClick={keepAlive} data-testid="firma-keepalive" className="mt-4 w-full h-12 rounded-xl bg-primary text-white font-bold text-base hover:opacity-90 transition-opacity">Sono qui, resta connesso</button>
          </div>
        </div>
      )}
    </>
  );
}

const shortName = (o) => `${o.nome} ${(o.cognome || "").charAt(0)}.`.trim();

export const OperatoreLocked = ({ name, testid }) => (
  <div data-testid={testid} className="flex items-center gap-2 h-10 px-3 rounded-md border bg-emerald-50 border-emerald-200 text-sm font-bold text-emerald-800">
    <Lock size={14} /> {name}
    <span className="ml-auto text-[10px] uppercase tracking-wide text-emerald-600 font-bold">Firma attiva</span>
  </div>
);

function LockScreen({ onUnlock, structureId, structureName, accountEmail, onLogout, restrictOperatoreId, logo }) {
  const [ops, setOps] = useState([]);
  const [sel, setSel] = useState(null);
  const [pin, setPin] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/firma/operatori", { params: structureId && structureId !== "all" ? { structure_id: structureId } : {} })
      .then((r) => {
        let list = r.data || [];
        if (restrictOperatoreId) list = list.filter((o) => o.id === restrictOperatoreId);
        setOps(list);
        // Nessuna auto-selezione: mostra sempre l'elenco così ognuno riseleziona il proprio nome.
      })
      .catch(() => setOps([]));
  }, [structureId, restrictOperatoreId]);

  const submit = async (p) => {
    if (!sel) return;
    setLoading(true); setErr("");
    try {
      const { data } = await api.post("/firma/verify", { operatore_id: sel.id, pin: p });
      onUnlock(data.operatore);
    } catch (e) {
      setErr("PIN errato, riprova");
      setPin("");
    } finally { setLoading(false); }
  };

  const press = (d) => {
    setErr("");
    const pinLen = (sel && sel.pin_len) || 2;
    setPin((prev) => {
      const np = (prev + String(d)).slice(0, pinLen);
      if (np.length === pinLen) setTimeout(() => submit(np), 60);
      return np;
    });
  };
  const back = () => { setErr(""); setPin((p) => p.slice(0, -1)); };

  // Inserimento PIN anche da tastiera fisica (oltre al tastierino a schermo)
  useEffect(() => {
    if (!sel) return;
    const onKey = (e) => {
      if (/^[0-9]$/.test(e.key)) { e.preventDefault(); press(Number(e.key)); }
      else if (e.key === "Backspace") { e.preventDefault(); back(); }
      else if (e.key === "Escape") { e.preventDefault(); setSel(null); setPin(""); setErr(""); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [sel]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{ background: "linear-gradient(135deg,#0f3d2e 0%,#123b52 100%)" }} data-testid="firma-lock">
      <div className="w-full max-w-3xl bg-white rounded-2xl shadow-2xl overflow-hidden">
        <div className="px-5 py-2.5 flex items-center justify-between bg-slate-50 border-b text-xs">
          <div className="flex items-center gap-1.5 font-bold text-slate-700" data-testid="firma-structure">
            <Building2 size={14} className="text-emerald-700" /> {structureName || "—"}
          </div>
          <div className="flex items-center gap-3">
            <span className="text-slate-500 hidden sm:block" data-testid="firma-account">Accesso: <b className="text-slate-700">{accountEmail}</b></span>
            <button onClick={onLogout} data-testid="firma-logout"
              className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-slate-200 hover:bg-slate-300 font-semibold text-slate-700 transition-colors">
              <LogOut size={13} /> Cambia login
            </button>
          </div>
        </div>
        <div className="px-6 py-4 flex items-center gap-3 text-white" style={{ background: "var(--gr-primary,#1f7a5c)" }}>
          <Fingerprint size={22} />
          <div>
            <div className="font-head font-bold text-lg leading-tight">Firma Rapida</div>
            <div className="text-xs opacity-90">Seleziona il tuo nome e inserisci il PIN per continuare</div>
          </div>
          {logo && <img src={logo} alt="Logo azienda" data-testid="firma-logo" className="ml-auto h-10 max-w-[140px] object-contain bg-white/95 rounded-md px-2 py-1" />}
        </div>

        {!sel ? (
          <div className="p-6">
            {ops.length === 0 ? (
              <div className="text-center text-muted-foreground py-10" data-testid="firma-no-ops">
                Nessun operatore attivo con PIN per questa struttura.<br />
                Chiedi al Responsabile di impostare un PIN dalla scheda operatore.
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 max-h-[420px] overflow-y-auto">
                {ops.map((o) => (
                  <button key={o.id} onClick={() => { setSel(o); setPin(""); setErr(""); }}
                    disabled={!o.has_pin}
                    data-testid={`firma-op-${o.id}`}
                    className={`p-4 rounded-xl border-2 text-left transition-colors ${o.has_pin ? "border-transparent bg-secondary hover:border-primary" : "border-dashed opacity-50 cursor-not-allowed"}`}>
                    <div className="font-head font-bold leading-tight">{o.cognome} {o.nome}</div>
                    <div className="text-xs text-muted-foreground">{fmtQual(o.qualifica) || "operatore"}</div>
                    {!o.has_pin && <div className="text-[10px] text-red-500 mt-1 font-semibold">PIN non impostato</div>}
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="p-6 flex flex-col items-center">
            <div className="text-sm text-muted-foreground">Operatore selezionato</div>
            <div className="font-head font-bold text-xl mb-1" data-testid="firma-selected">{sel.cognome} {sel.nome}</div>
            <div className="flex items-center gap-3 my-4">
              {Array.from({ length: (sel && sel.pin_len) || 2 }).map((_, i) => (
                <div key={i} className="w-14 h-16 rounded-xl border-2 grid place-items-center text-3xl font-bold tabnum"
                  style={{ borderColor: pin.length > i ? "var(--gr-primary,#1f7a5c)" : "#e2e8f0" }}>
                  {pin.length > i ? "•" : ""}
                </div>
              ))}
            </div>
            {err && <div className="text-red-600 text-sm font-semibold mb-2" data-testid="firma-error">{err}</div>}
            <div className="grid grid-cols-3 gap-2.5 w-full max-w-[300px] mx-auto">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
                <button key={n} onClick={() => press(n)} disabled={loading} data-testid={`firma-key-${n}`}
                  className="h-16 rounded-2xl bg-secondary hover:bg-primary hover:text-white text-2xl font-bold transition-colors touch-manipulation active:bg-primary active:text-white">{n}</button>
              ))}
              <button onClick={() => { setSel(null); setPin(""); setErr(""); }} data-testid="firma-back-list"
                className="h-16 rounded-2xl bg-secondary hover:bg-muted text-sm font-semibold transition-colors touch-manipulation">Indietro</button>
              <button onClick={() => press(0)} disabled={loading} data-testid="firma-key-0"
                className="h-16 rounded-2xl bg-secondary hover:bg-primary hover:text-white text-2xl font-bold transition-colors touch-manipulation active:bg-primary active:text-white">0</button>
              <button onClick={back} disabled={loading} data-testid="firma-del"
                className="h-16 rounded-2xl bg-secondary hover:bg-muted grid place-items-center transition-colors touch-manipulation"><Delete size={22} /></button>
            </div>
            {loading && <div className="mt-4 text-sm text-muted-foreground flex items-center gap-2"><ShieldCheck size={16} /> Verifica in corso…</div>}
          </div>
        )}
      </div>
    </div>
  );
}

export { shortName };

export const currentTurno = () => { const h = new Date().getHours(); if (h >= 7 && h < 14) return "mattina"; if (h >= 14 && h < 20) return "pomeriggio"; return "notte"; };
